/* ==========================================================================
   BN Free Gift & Spend Threshold Rewards -- milestone fireworks
   --------------------------------------------------------------------------
   Fires a firework burst the moment the cart crosses $100 (free gift) or $200
   ($20 off). Purely decorative: it reads the same data attributes Liquid
   already renders and never touches the cart.

   WHEN IT FIRES
   -------------
   On the crossing, not on the state. Milestones already celebrated are kept in
   sessionStorage, so a customer who reloads /cart, or comes back from
   checkout, does not get the same burst again. Dropping back below a threshold
   clears its flag, so genuinely re-earning it celebrates again.

   Absence of stored state counts as "not yet reached". That is deliberate: a
   customer who adds items on product pages and then lands on /cart already
   over $100 has crossed the line without this script watching, and that is
   the single most common way the milestone gets hit. Seeding silently would
   swallow the celebration in exactly the case it matters most.

   The canvas is appended to document.body, not to the rewards box, because the
   cart page re-renders that box on every quantity change and would otherwise
   destroy the animation halfway through.
   ========================================================================== */

(function () {
  'use strict';

  var STORE_KEY = 'bn-gift-milestones';

  var TIERS = {
    gift: {
      /* Position along the progress bar, matching the milestone dots that
         snippets/bn-gift-rewards.liquid places at 50% and 100%. */
      at: 0.5,
      shells: 1,
      particles: 70,
      power: 5.4,
      colors: ['#1f9d55', '#4ade80', '#a7f3d0', '#ffffff']
    },
    discount: {
      at: 1,
      shells: 3,
      particles: 90,
      power: 6.6,
      colors: ['#d99b1c', '#fbbf24', '#fde68a', '#ffffff', '#1f9d55']
    }
  };

  var GRAVITY = 0.045;
  var DRAG = 0.985;
  var FADE = 0.012;

  var canvas = null;
  var ctx = null;
  var particles = [];
  var frame = null;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function readState() {
    try {
      return JSON.parse(sessionStorage.getItem(STORE_KEY)) || {};
    } catch (error) {
      /* Private mode, blocked storage, corrupted value -- all non-fatal. The
         worst outcome is a repeated firework, so there is nothing to report. */
      return {};
    }
  }

  function writeState(state) {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify(state));
    } catch (error) {
      /* Ignore -- see readState. */
    }
  }

  /* ---------------------------------------------------------------- canvas */

  function ensureCanvas() {
    if (canvas) return;

    canvas = document.createElement('canvas');
    canvas.className = 'bn-fx-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.appendChild(canvas);

    ctx = canvas.getContext('2d');
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
  }

  function resizeCanvas() {
    if (!canvas) return;

    var ratio = window.devicePixelRatio || 1;
    canvas.width = window.innerWidth * ratio;
    canvas.height = window.innerHeight * ratio;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  }

  function teardown() {
    if (frame) cancelAnimationFrame(frame);
    frame = null;
    particles = [];

    window.removeEventListener('resize', resizeCanvas);

    if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
    canvas = null;
    ctx = null;
  }

  /* ------------------------------------------------------------- particles */

  function explode(x, y, tier) {
    for (var i = 0; i < tier.particles; i++) {
      /* Even angular spread with a jittered radius gives a rounder shell than
         randomising the angle, which tends to clump. */
      var angle = (Math.PI * 2 * i) / tier.particles + Math.random() * 0.12;
      var speed = tier.power * (0.45 + Math.random() * 0.55);

      particles.push({
        x: x,
        y: y,
        px: x,
        py: y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1,
        decay: FADE * (0.7 + Math.random() * 0.8),
        size: 1 + Math.random() * 1.6,
        color: tier.colors[(Math.random() * tier.colors.length) | 0]
      });
    }
  }

  function tick() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (var i = particles.length - 1; i >= 0; i--) {
      var p = particles[i];

      p.px = p.x;
      p.py = p.y;
      p.vx *= DRAG;
      p.vy = p.vy * DRAG + GRAVITY;
      p.x += p.vx;
      p.y += p.vy;
      p.life -= p.decay;

      if (p.life <= 0) {
        particles.splice(i, 1);
        continue;
      }

      /* Drawn as a short segment from the previous position rather than a dot,
         which reads as a spark trail without the cost of a real trail buffer. */
      ctx.globalAlpha = Math.max(p.life, 0);
      ctx.strokeStyle = p.color;
      ctx.lineWidth = p.size;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(p.px, p.py);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    }

    ctx.globalAlpha = 1;

    if (particles.length) {
      frame = requestAnimationFrame(tick);
    } else {
      teardown();
    }
  }

  /* ---------------------------------------------------------- celebration */

  function celebrate(name, element) {
    var tier = TIERS[name];
    if (!tier || prefersReducedMotion() || document.hidden) return;

    var bar = element.querySelector('.bn-rewards__bar');
    if (!bar) return;

    var rect = bar.getBoundingClientRect();
    if (!rect.width) return;

    var x = rect.left + rect.width * tier.at;
    var y = rect.top + rect.height / 2;

    /* Pulse the milestone dot itself so the win is legible even for someone
       who looks up after the sparks have gone. */
    var dots = element.querySelectorAll('.bn-rewards__node');
    var dot = name === 'discount' ? dots[dots.length - 1] : dots[0];
    if (dot) {
      dot.classList.remove('bn-is-popping');
      void dot.offsetWidth;
      dot.classList.add('bn-is-popping');
    }

    ensureCanvas();

    for (var shell = 0; shell < tier.shells; shell++) {
      if (shell === 0) {
        explode(x, y, tier);
      } else {
        /* Extra shells drift up and out so three bursts do not stack into one
           blob. Offsets are clamped to the viewport. */
        (function (index) {
          setTimeout(function () {
            if (!canvas) ensureCanvas();

            var spread = rect.width * 0.28;
            var ox = Math.min(
              Math.max(x + (index % 2 === 0 ? -spread : spread), 20),
              window.innerWidth - 20
            );
            var oy = Math.max(y - 40 - index * 28, 20);

            explode(ox, oy, tier);

            if (!frame) frame = requestAnimationFrame(tick);
          }, index * 260);
        })(shell);
      }
    }

    if (!frame) frame = requestAnimationFrame(tick);
  }

  /* --------------------------------------------------------------- observe */

  function num(element, key) {
    var value = parseInt(element.dataset[key], 10);
    return isNaN(value) ? 0 : value;
  }

  function check() {
    var element = document.querySelector('bn-gift-rewards');
    if (!element) return;

    var total = num(element, 'qualifyingTotal');
    var reached = {
      gift: total >= num(element, 'giftThreshold'),
      discount: total >= num(element, 'discountThreshold')
    };

    var previous = readState();

    Object.keys(reached).forEach(function (name) {
      if (reached[name] && !previous[name]) celebrate(name, element);
    });

    writeState(reached);
  }

  /* The cart page swaps this section's markup on cart:updated, so the check has
     to run after that swap -- a task queued now lands after the synchronous
     listener that does the replacing. */
  document.addEventListener('cart:updated', function () {
    setTimeout(check, 0);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', check);
  } else {
    check();
  }
})();
